import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Stage, Layer, Image as KonvaImage, Rect, Ellipse, Group } from 'react-konva';
import { ZoomIn, ZoomOut, Check, Sliders, Compass, Filter, RefreshCcw, Move, Camera, Trash2 } from 'lucide-react';
import Konva from 'konva';
import { useDesignContext } from '../../hooks/useDesignContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  font-family: 'Inter', sans-serif;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome/Safari/Edge */
  }
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 800;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const ControlGroup = styled.div`
  background: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  padding: 8px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${props => props.$active ? '#5D722C' : '#e2e8f0'};
  background: ${props => props.$active ? '#f1f5e9' : '#ffffff'};
  color: ${props => props.$active ? '#5D722C' : '#475569'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    background: #f8fafc;
  }
`;

const RangeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RangeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  span:first-child {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
  }
  
  span:last-child {
    font-size: 12px;
    font-weight: 700;
    color: #5D722C;
    background: #f1f5e9;
    padding: 2px 8px;
    border-radius: 6px;
  }
`;

const StyledRange = styled.input`
  width: 100%;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  appearance: none;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 20px;
    height: 20px;
    background: #5D722C;
    border: 3px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    transition: transform 0.1s;
  }
  
  &::-webkit-slider-thumb:hover {
    transform: scale(1.1);
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const IconButton = styled.button`
  flex: 1;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #cbd5e1;
  }
`;

const Footer = styled.div`
  padding: 10px 16px;
  border-top: 1px solid #f1f5f9;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MainButton = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost' }>`
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;

  ${props => props.$variant === 'primary' ? css`
    background: #5D722C;
    color: #ffffff;
    box-shadow: 0 10px 20px -5px rgba(93, 114, 44, 0.4);
    &:hover {
      transform: translateY(-2px);
      filter: brightness(1.1);
      box-shadow: 0 15px 30px -5px rgba(93, 114, 44, 0.5);
    }
  ` : props.$variant === 'danger' ? css`
    background: #fee2e2;
    color: #ef4444;
    border: 1px solid #fecaca;
    &:hover { background: #fef2f2; transform: translateY(-1px); }
  ` : css`
    background: #eaf4d6;
    color: #2f4a1c;
    border: 1px solid #b8cc8a;
    &:hover { background: #dcebbd; color: #243815; border-color: #9fbc69; }
  `}
`;

const CanvasWrapper = styled.div`
  width: 100%;
  height: 200px;
  max-width: 320px;
  margin: 0 auto;
  flex-shrink: 0;
  background: #f1f5f9;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  border: 1px solid #e2e8f0;
  cursor: grab;
  touch-action: none;
  &:active { cursor: grabbing; }
`;

const FloatingLabel = styled.div`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(8px);
  color: #ffffff;
  padding: 6px 14px;
  border-radius: 30px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  pointer-events: none;
  z-index: 5;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
`;

const UploadBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 60px 20px;
  border: 2px dashed #cbd5e1;
  border-radius: 20px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  background: #f8fafc;

  &:hover {
    border-color: #5D722C;
    background: #f1f5e9;
    color: #5D722C;
    transform: scale(0.99);
  }
`;

interface PorcelainProps {
    onClose: () => void;
}

const Porcelain: React.FC<PorcelainProps> = ({ onClose }) => {
    const { currentDesign, updateDesign, pixelsPerInch, placementSlots, renderedTemplateOrigin, activePlacementSlot, setActivePlacementSlot } = useDesignContext();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Find existing porcelain element
    const porcelainEl = useMemo(() =>
        currentDesign.elements.find(el => el.properties?.role === 'porcelain'),
        [currentDesign.elements]
    );

    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [stageSize, setStageSize] = useState({ width: 286, height: 200 });
    const [initialized, setInitialized] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<any>(null);

    // Adjustment states
    const [maskType, setMaskType] = useState<'oval' | 'circle'>(porcelainEl?.properties?.shape || 'oval');
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [isGreyscale, setIsGreyscale] = useState(false);
    const [posOffset, setPosOffset] = useState({ x: 0, y: 0 });

    // Calculate porcelain size strictly from the model size DB values (inches) — no fallbacks
    const cropAreaSize = useMemo(() => {
        const widthIn = currentDesign.porcelain_width_in;
        const heightIn = currentDesign.porcelain_height_in;

        if (!widthIn || !heightIn) return { w: 0, h: 0 };

        // Convert inches to pixels directly using pixelsPerInch
        const ppi = pixelsPerInch || 96;
        const widthPx = Math.round(widthIn * ppi);
        const heightPx = Math.round(heightIn * ppi);

        return { w: widthPx, h: heightPx };
    }, [currentDesign.porcelain_width_in, currentDesign.porcelain_height_in, pixelsPerInch]);

    // Load image and saved adjustments
    useEffect(() => {
        const src = porcelainEl?.properties?.originalSrc || porcelainEl?.properties?.src;
        if (!src) {
            setImage(null);
            return;
        }

        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            setImage(img);

            const adj = porcelainEl?.properties?.adjustment;

            // Calculate minimum zoom to cover mask
            const minZ = Math.max(cropAreaSize.w / img.width, cropAreaSize.h / img.height);

            // Set zoom from saved or use min zoom
            setZoom(adj?.zoom || minZ);

            // Set rotation from saved
            setRotation(adj?.rotation || 0);

            // Set greyscale from saved
            setIsGreyscale(adj?.isGreyscale || false);

            // Set position from saved - IMPORTANT: This ensures position is restored
            // For new uploads, apply smart positioning for portrait photos
            let initialX = adj?.xOffset || 0;
            let initialY = adj?.yOffset || 0;

            // Apply smart centering unless user has manually adjusted position
            const hasManualAdjustment = adj?.manuallyAdjusted === true;

            if (!hasManualAdjustment) {
                const imgRatio = img.width / img.height;

                // If image is portrait-oriented (like typical headshots)
                if (imgRatio < 1.0) {
                    // Move image DOWN by ~20% of the scaled image height to center the face
                    // This ensures the full head is visible while centering the face in the oval
                    const scaledHeight = img.height * minZ;
                    initialY = scaledHeight * 0.20;
                }
            }

            setPosOffset({
                x: initialX,
                y: initialY
            });

            setInitialized(true);
        };
        img.src = src;
    }, [porcelainEl]);

    const imageRef = useRef<any>(null);
    const clippedImageRef = useRef<any>(null);

    // Apply grayscale filter
    useEffect(() => {
        if (image && initialized) {
            const nodes = [imageRef.current, clippedImageRef.current];
            nodes.forEach(node => {
                if (node) {
                    if (isGreyscale) {
                        try {
                            node.filters([Konva.Filters.Grayscale]);
                            node.cache();
                        } catch (e) { }
                    } else {
                        node.filters([]);
                        try { node.clearCache(); } catch (e) { }
                    }
                    node.getLayer()?.batchDraw();
                }
            });
        }
    }, [image, isGreyscale, initialized]);

    // Handle resize
    useEffect(() => {
        if (!containerRef.current) return;

        const updateSize = () => {
            if (containerRef.current) {
                const w = containerRef.current.clientWidth;
                const h = containerRef.current.clientHeight;
                if (w > 0 && h > 0) {
                    setStageSize({ width: w, height: h });
                }
            }
        };

        const resizeObserver = new ResizeObserver(updateSize);
        resizeObserver.observe(containerRef.current);
        updateSize();
        return () => resizeObserver.disconnect();
    }, []);

    // Calculate mask dimensions
    const activeMaskSize = useMemo(() => {
        if (maskType === 'circle') {
            const r = Math.min(cropAreaSize.w, cropAreaSize.h);
            return { w: r, h: r };
        }
        return { w: cropAreaSize.w, h: cropAreaSize.h };
    }, [maskType]);

    // Calculate visual scale for display
    const visualScale = useMemo(() => {
        const padding = 20;
        const availableW = stageSize.width - padding * 2;
        const availableH = stageSize.height - padding * 2;
        if (availableW <= 0 || availableH <= 0) return 1;
        const scaleX = availableW / activeMaskSize.w;
        const scaleY = availableH / activeMaskSize.h;
        // Use the smaller scale to ensure the mask fits completely
        return Math.min(scaleX, scaleY);
    }, [stageSize, activeMaskSize]);

    // Calculate minimum zoom to cover mask
    const minZoom = useMemo(() => {
        if (!image) return 0.1;
        return Math.max(activeMaskSize.w / image.width, activeMaskSize.h / image.height);
    }, [image, activeMaskSize]);

    // Handle drag with constraints
    const handleDrag = (e: any) => {
        if (!image) return;

        const node = e.target;
        let newX = node.x();
        let newY = node.y();

        // Calculate bounds based on current zoom
        const scaledW = image.width * zoom;
        const scaledH = image.height * zoom;

        // Maximum offset allowed (image must cover mask)
        const maxOffsetX = Math.max(0, (scaledW - activeMaskSize.w) / 2);
        const maxOffsetY = Math.max(0, (scaledH - activeMaskSize.h) / 2);

        // Clamp values
        newX = Math.max(-maxOffsetX, Math.min(maxOffsetX, newX));
        newY = Math.max(-maxOffsetY, Math.min(maxOffsetY, newY));

        setPosOffset({ x: newX, y: newY });
        node.position({ x: newX, y: newY });
    };

    // Constrain position when zoom changes
    useEffect(() => {
        if (!image || !initialized) return;

        const scaledW = image.width * zoom;
        const scaledH = image.height * zoom;

        const maxOffsetX = Math.max(0, (scaledW - activeMaskSize.w) / 2);
        const maxOffsetY = Math.max(0, (scaledH - activeMaskSize.h) / 2);

        setPosOffset(prev => ({
            x: Math.max(-maxOffsetX, Math.min(maxOffsetX, prev.x)),
            y: Math.max(-maxOffsetY, Math.min(maxOffsetY, prev.y))
        }));
    }, [zoom, image, activeMaskSize, initialized]);

    const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const dataUrl = e.target?.result as string;

            const img = new window.Image();
            img.onload = () => {
                updateDesign(prev => {
                    const nextElements = [...prev.elements];
                    // Remove any existing porcelain element
                    const pIdx = nextElements.findIndex(el => el.properties?.role === 'porcelain');
                    if (pIdx !== -1) nextElements.splice(pIdx, 1);

                    let finalX = 0, finalY = 0;
                    let finalW = cropAreaSize.w;
                    let finalH = cropAreaSize.h;
                    let finalSlotType: string | null = null;
                    let isFixed = false;

                    // Priority 1: Use activePlacementSlot if it's a porcelain slot (most reliable)
                    const activeIsPortrait = activePlacementSlot?.slot_type === 'porcelain';
                    const template = nextElements.find(el => el.properties?.role === 'template' || el.properties?.isTemplate === true);

                    if (activeIsPortrait && template) {
                        const ox = renderedTemplateOrigin?.x ?? template.x;
                        const oy = renderedTemplateOrigin?.y ?? template.y;
                        const ppi = pixelsPerInch || 96;
                        finalX = ox + Number(activePlacementSlot!.v1) * ppi;
                        finalY = oy + Number(activePlacementSlot!.h1) * ppi;
                        finalW = (Number(activePlacementSlot!.v2) - Number(activePlacementSlot!.v1)) * ppi;
                        finalH = (Number(activePlacementSlot!.h2) - Number(activePlacementSlot!.h1)) * ppi;
                        finalSlotType = 'porcelain';
                        isFixed = true;
                    } else {
                        // Priority 2: Look up the porcelain slot via the special artwork's placement code
                        const specialArt = nextElements.find((el: any) =>
                            el.properties?.slotType === 'special' || el.properties?.placement === 'special'
                        );
                        if (specialArt && template) {
                            const slot = placementSlots?.find((s: any) =>
                                s.slot_type === 'porcelain' && s.placement_code === specialArt.properties?.placementCode
                            );
                            if (slot) {
                                const ox = renderedTemplateOrigin?.x ?? template.x;
                                const oy = renderedTemplateOrigin?.y ?? template.y;
                                const ppi = pixelsPerInch || 96;
                                finalX = ox + Number(slot.v1) * ppi;
                                finalY = oy + Number(slot.h1) * ppi;
                                finalW = (Number(slot.v2) - Number(slot.v1)) * ppi;
                                finalH = (Number(slot.h2) - Number(slot.h1)) * ppi;
                                finalSlotType = 'porcelain';
                                isFixed = true;
                            }
                        }
                        // NOTE: No fallback center-of-stone positioning.
                        // If no slot is found, the element is still created at (0,0) with default size,
                        // but in practice this should not happen when a special artwork is placed.
                        if (!isFixed && template) {
                            // Last resort: place on the left side of the stone (22% from left) to avoid overlap with center text and stay inside shaped stone edges
                            const stoneX = renderedTemplateOrigin?.x ?? template.x ?? 0;
                            const stoneY = renderedTemplateOrigin?.y ?? template.y ?? 0;
                            const stoneW = template.width || 800;
                            const stoneH = template.height || 600;
                            finalX = Math.floor(stoneX + stoneW * 0.22);
                            finalY = Math.floor(stoneY + (stoneH - finalH) / 2);
                        }
                    }

                    const el = {
                        id: `porcelain-${Date.now()}`,
                        type: 'artwork' as const,
                        side: prev.activeSide || 'front',
                        x: finalX, y: finalY, width: finalW, height: finalH,
                        rotation: 0,
                        properties: {
                            role: 'porcelain',
                            src: dataUrl,
                            originalSrc: dataUrl,
                            shape: 'oval',
                            slotType: finalSlotType,
                            isFixedPosition: isFixed,
                            draggable: !isFixed,
                            adjustment: {
                                zoom: 0,
                                rotation: 0,
                                isGreyscale: false,
                                xOffset: 0,
                                yOffset: 0,
                                maskType: 'oval'
                            }
                        }
                    };
                    return { ...prev, elements: [...nextElements, el] };
                });

                // Clear the active placement slot so the outline box disappears
                if (setActivePlacementSlot) setActivePlacementSlot(null);
            };
            img.src = dataUrl;
        };
        reader.readAsDataURL(file);
    };

    const handleSave = () => {
        if (!image) return;

        // Create export canvas
        const exportCanvas = document.createElement('canvas');
        const exportW = activeMaskSize.w * 2;
        const exportH = activeMaskSize.h * 2;
        exportCanvas.width = exportW;
        exportCanvas.height = exportH;
        const ctx = exportCanvas.getContext('2d');
        if (!ctx) return;

        // Clear canvas for transparency (no white background)
        ctx.clearRect(0, 0, exportW, exportH);

        // 1. Apply shape mask clipping path (oval or circle) in unscaled canvas space
        ctx.save();
        ctx.beginPath();
        if (maskType === 'circle') {
            ctx.arc(exportW / 2, exportH / 2, Math.min(exportW, exportH) / 2, 0, Math.PI * 2);
        } else {
            ctx.ellipse(exportW / 2, exportH / 2, exportW / 2, exportH / 2, 0, 0, Math.PI * 2);
        }
        ctx.clip();

        // 2. Scale factor for 2x export canvas (zoom in state is absolute scale for cropAreaSize)
        const finalScale = zoom * 2;

        // 3. Center coordinate system and apply user drag/rotation/scale
        ctx.translate(exportW / 2 + posOffset.x * 2, exportH / 2 + posOffset.y * 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.scale(finalScale, finalScale);

        // Apply grayscale if needed
        if (isGreyscale) {
            ctx.filter = 'grayscale(100%)';
        }

        // Draw image centered
        ctx.drawImage(image, -image.width / 2, -image.height / 2);
        ctx.restore();

        // Get cropped image data
        const croppedUrl = exportCanvas.toDataURL('image/png');

        updateDesign(prev => ({
            ...prev,
            elements: prev.elements.map(el =>
                el.id === porcelainEl?.id
                    ? {
                        ...el,
                        properties: {
                            ...el.properties,
                            src: croppedUrl,
                            shape: maskType,
                            adjustment: {
                                zoom,
                                rotation,
                                isGreyscale,
                                xOffset: posOffset.x,
                                yOffset: posOffset.y,
                                maskType,
                                manuallyAdjusted: true
                            }
                        }
                    }
                    : el
            )
        }));
        // Clear the placement slot outline after saving so only the photo remains visible
        if (setActivePlacementSlot) setActivePlacementSlot(null);
        onClose();
    };

    const resetAdjustments = () => {
        if (!image) return;

        const minZ = Math.max(activeMaskSize.w / image.width, activeMaskSize.h / image.height);
        setZoom(minZ);
        setRotation(0);
        setPosOffset({ x: 0, y: 0 });
        setIsGreyscale(false);
    };

    return (
        <Container>
            <Content>
                {!image ? (
                    <UploadBox onClick={() => fileInputRef.current?.click()}>
                        <Camera size={32} />
                        <div style={{ fontWeight: 700, fontSize: 16 }}>Upload Photo</div>
                        <div style={{ fontSize: 13, textAlign: 'center' }}>
                            Click here to select a JPG or PNG photo for the porcelain.
                        </div>
                    </UploadBox>
                ) : (
                    <>
                        <CanvasWrapper ref={containerRef}>
                            <Stage
                                width={stageSize.width}
                                height={stageSize.height}
                                ref={stageRef}
                                onMouseDown={(e) => e.cancelBubble = true}
                            >
                                <Layer>
                                    <Group
                                        x={stageSize.width / 2}
                                        y={stageSize.height / 2}
                                        scaleX={visualScale}
                                        scaleY={visualScale}
                                    >
                                        {/* Background image (semi-transparent) */}
                                        <KonvaImage
                                            ref={imageRef}
                                            image={image}
                                            x={posOffset.x}
                                            y={posOffset.y}
                                            width={image.width}
                                            height={image.height}
                                            offsetX={image.width / 2}
                                            offsetY={image.height / 2}
                                            scaleX={zoom}
                                            scaleY={zoom}
                                            rotation={rotation}
                                            opacity={0.3}
                                            listening={false}
                                        />

                                        {/* Mask group */}
                                        <Group>
                                            {/* Dark overlay */}
                                            <Rect
                                                x={-stageSize.width / visualScale}
                                                y={-stageSize.height / visualScale}
                                                width={stageSize.width * 2 / visualScale}
                                                height={stageSize.height * 2 / visualScale}
                                                fill="rgba(0, 0, 0, 0.6)"
                                            />

                                            {/* Clipping group for the image */}
                                            <Group clipFunc={(ctx) => {
                                                ctx.beginPath();
                                                if (maskType === 'oval') {
                                                    ctx.ellipse(0, 0, cropAreaSize.w / 2, cropAreaSize.h / 2, 0, 0, Math.PI * 2);
                                                } else {
                                                    const r = Math.min(cropAreaSize.w, cropAreaSize.h) / 2;
                                                    ctx.ellipse(0, 0, r, r, 0, 0, Math.PI * 2);
                                                }
                                                ctx.closePath();
                                            }}>
                                                {/* White background for the mask area */}
                                                <Rect
                                                    x={-cropAreaSize.w / 2}
                                                    y={-cropAreaSize.h / 2}
                                                    width={cropAreaSize.w}
                                                    height={cropAreaSize.h}
                                                    fill="#ffffff"
                                                />

                                                {/* Clipped image */}
                                                <KonvaImage
                                                    ref={clippedImageRef}
                                                    image={image}
                                                    x={posOffset.x}
                                                    y={posOffset.y}
                                                    width={image.width}
                                                    height={image.height}
                                                    offsetX={image.width / 2}
                                                    offsetY={image.height / 2}
                                                    scaleX={zoom}
                                                    scaleY={zoom}
                                                    rotation={rotation}
                                                    draggable
                                                    onDragMove={handleDrag}
                                                />
                                            </Group>

                                            {/* Mask outline */}
                                            <Ellipse
                                                x={0}
                                                y={0}
                                                radiusX={maskType === 'oval' ? cropAreaSize.w / 2 : Math.min(cropAreaSize.w, cropAreaSize.h) / 2}
                                                radiusY={maskType === 'oval' ? cropAreaSize.h / 2 : Math.min(cropAreaSize.w, cropAreaSize.h) / 2}
                                                stroke="#5D722C"
                                                strokeWidth={3 / visualScale}
                                                dash={[10 / visualScale, 8 / visualScale]}
                                                listening={false}
                                            />
                                        </Group>
                                    </Group>
                                </Layer>
                            </Stage>
                        </CanvasWrapper>

                        <Section>
                            <MainButton
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                    background: '#f8fafc',
                                    color: '#64748b',
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    padding: '10px 16px'
                                }}
                            >
                                <Camera size={18} /> Upload New Photo
                            </MainButton>
                        </Section>

                        <Section>
                            <SectionHeader>
                                <Compass size={12} /> Adjust Image
                            </SectionHeader>
                            <ControlGroup>
                                <RangeWrapper>
                                    <RangeHeader>
                                        <span>Zoom Scale</span>
                                        <span>{Math.round((zoom / minZoom) * 100)}%</span>
                                    </RangeHeader>
                                    <StyledRange
                                        type="range"
                                        min={minZoom}
                                        max={3}
                                        step={0.01}
                                        value={zoom}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setZoom(Number(e.target.value))
                                        }
                                    />
                                    <QuickActions>
                                        <IconButton onClick={() => setZoom((z: number) => Math.max(minZoom, z - 0.2))}>
                                            <ZoomOut size={16} />
                                        </IconButton>
                                        <IconButton onClick={() => setZoom(minZoom)}>
                                            Fill
                                        </IconButton>
                                        <IconButton onClick={() => setZoom((z: number) => Math.min(3, z + 0.2))}>
                                            <ZoomIn size={16} />
                                        </IconButton>
                                    </QuickActions>
                                </RangeWrapper>

                                <RangeWrapper style={{ marginTop: 8 }}>
                                    <RangeHeader>
                                        <span>Rotation</span>
                                        <span>{rotation}°</span>
                                    </RangeHeader>
                                    <StyledRange
                                        type="range"
                                        min={-180}
                                        max={180}
                                        step={1}
                                        value={rotation}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setRotation(Number(e.target.value))
                                        }
                                    />
                                    <QuickActions>
                                        <IconButton onClick={() => setRotation((r: number) => r - 45)}>
                                            -45°
                                        </IconButton>
                                        <IconButton onClick={() => setRotation(0)}>
                                            0°
                                        </IconButton>
                                        <IconButton onClick={() => setRotation((r: number) => r + 45)}>
                                            +45°
                                        </IconButton>
                                    </QuickActions>
                                </RangeWrapper>
                            </ControlGroup>
                        </Section>

                        <Section>
                            <SectionHeader>
                                <Filter size={12} /> Color Effect
                            </SectionHeader>
                            <FilterGrid>
                                <FilterButton
                                    $active={!isGreyscale}
                                    onClick={() => setIsGreyscale(false)}
                                >
                                    Color
                                </FilterButton>
                                <FilterButton
                                    $active={isGreyscale}
                                    onClick={() => setIsGreyscale(true)}
                                >
                                    Grayscale
                                </FilterButton>
                            </FilterGrid>
                        </Section>

                        <Section>
                            <SectionHeader>
                                <Sliders size={12} /> Quick Actions
                            </SectionHeader>
                            <QuickActions>
                                <IconButton onClick={resetAdjustments}>
                                    <RefreshCcw size={16} /> Reset
                                </IconButton>
                            </QuickActions>
                        </Section>
                    </>
                )}
            </Content>

            <Footer>
                <MainButton $variant="primary" disabled={!image} onClick={handleSave}>
                    <Check size={20} /> Apply & Save Photo
                </MainButton>
                <div style={{ display: 'flex', gap: 10 }}>
                    <MainButton onClick={onClose} style={{ flex: 1 }}>
                        Cancel
                    </MainButton>
                </div>
            </Footer>

            <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                style={{ display: 'none' }}
            />
        </Container>
    );
};

export default Porcelain;